import { useEffect, useState } from 'react';
import type { ReportReason } from '@brainfart/shared';
import { Sheet, SheetBody } from './Sheet';
import { closeReport, openReport, submitReport, useReportTarget, type ReportTarget } from '../reportQuestion';
import { track } from '../telemetry';

const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'wrong_answer', label: '❌ The answer is wrong' },
  { value: 'multiple_correct', label: '🤷 More than one answer is right' },
  { value: 'outdated', label: '📅 It\'s out of date' },
  { value: 'unclear', label: '😵 Confusing or has a typo' },
  { value: 'offensive', label: '🚫 Offensive' },
];

// Small text button shown once an answer is revealed.
export function ReportButton({ target, onOpen }: { target: ReportTarget; onOpen?: () => void }) {
  return (
    <button
      onClick={() => {
        onOpen?.();
        track({ type: 'report_opened', payload: { questionId: target.questionId, mode: target.mode } });
        openReport(target);
      }}
      style={{ alignSelf: 'center', marginTop: '0.75rem', background: 'none', border: 'none', color: '#777', fontSize: '0.8rem', cursor: 'pointer', padding: '0.4rem' }}
    >
      🚩 Report question
    </button>
  );
}

type Step = 'pick' | 'sending' | 'sent' | 'failed';

// Mounted once at the root.
export function ReportQuestionHost() {
  const target = useReportTarget();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [comment, setComment] = useState('');
  const [step, setStep] = useState<Step>('pick');

  useEffect(() => {
    setReason(null);
    setComment('');
    setStep('pick');
  }, [target?.questionId]);

  useEffect(() => {
    if (step !== 'sent') return;
    const t = setTimeout(closeReport, 1800);
    return () => clearTimeout(t);
  }, [step]);

  const send = async () => {
    if (!target || !reason) return;
    setStep('sending');
    try {
      await submitReport(target, reason, comment);
      track({ type: 'question_reported', payload: { questionId: target.questionId, reason, mode: target.mode } });
      setStep('sent');
    } catch {
      setStep('failed');
    }
  };

  return (
    <Sheet open={target !== null} onClose={closeReport} label="Report question">
      {target && step === 'sent' && (
        <SheetBody icon="🙏" title="Thanks for flagging it!" body="We'll check this question and fix it if it's wrong." />
      )}

      {target && step !== 'sent' && (
        <SheetBody icon="🚩" title="What's wrong with this one?" body={<span style={{ fontStyle: 'italic' }}>"{target.prompt}"</span>}>
          <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', textAlign: 'left' }}>
            {REASONS.map(r => (
              <button
                key={r.value}
                role="radio"
                aria-checked={reason === r.value}
                onClick={() => setReason(r.value)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '12px',
                  border: `1px solid ${reason === r.value ? '#FFC93C' : '#2E2B45'}`,
                  background: reason === r.value ? 'rgba(255,201,60,0.12)' : '#100E22',
                  color: '#eee',
                  fontSize: '0.95rem',
                  textAlign: 'left',
                  cursor: 'pointer',
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value.slice(0, 300))}
            placeholder="Anything else? (optional)"
            rows={2}
            style={{
              width: '100%',
              marginTop: '0.25rem',
              padding: '0.7rem 0.85rem',
              borderRadius: '12px',
              border: '1px solid #2E2B45',
              background: '#100E22',
              color: '#fff',
              fontSize: '0.9rem',
              resize: 'none',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          {step === 'failed' && <p style={{ color: '#FF6B6B', fontSize: '0.85rem' }}>Couldn't send that. Check your connection and try again.</p>}
          <button className="btn btn-primary" onClick={send} disabled={!reason || step === 'sending'}>
            {step === 'sending' ? 'Sending…' : 'Send report'}
          </button>
          <button className="btn btn-ghost" onClick={closeReport}>Cancel</button>
        </SheetBody>
      )}
    </Sheet>
  );
}
